import { useCallback, useMemo } from 'react';

// Types
import type { SpGroupType } from '@/hooks/swr/group/useSPGroups';
// Hooks
import useSPGroups from '@/hooks/swr/group/useSPGroups';

// Store
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

const useNewActiveGroup = () => {
    const {
        activeSocialProfileId,
        activeGroupId,
        manageGroups,
        resetActiveGroup,
        resetActiveSocialProfile
    } = useProfilesStore((state) => state);

    const { spGroups } = useSPGroups({
        revalidateIfStale: false
    });

    const activeGroup: SpGroupType = useMemo(() => {
        if (spGroups?.length && activeGroupId) {
            return spGroups.find((group) => group.id === activeGroupId);
        }
        return null;
    }, [activeGroupId, JSON.stringify(spGroups)]);

    const setActiveGroup = useCallback(
        (group: SpGroupType) => manageGroups(group),

        [activeGroupId]
    );

    const removeActiveSocialProfileFromGroup = useCallback(
        (comparisonData: SpGroupType['social_profiles']) => {
            if (activeSocialProfileId) {
                const activeProfile = comparisonData.find(
                    (socialProfile) => socialProfile.id === activeSocialProfileId
                );

                if (!activeProfile?.active) {
                    resetActiveSocialProfile();
                }
            }
        },

        [activeSocialProfileId]
    );

    return {
        newActiveGroup: activeGroup,
        setNewActiveGroup: setActiveGroup,
        resetActiveGroup,
        removeNewActiveSocialProfileFromGroup: removeActiveSocialProfileFromGroup
    };
};

const useActiveGroup = () => {
    const {
        newActiveGroup,
        setNewActiveGroup,
        resetActiveGroup,
        removeNewActiveSocialProfileFromGroup
    } = useNewActiveGroup();

    return {
        activeGroup: newActiveGroup,
        setActiveGroup: setNewActiveGroup,
        updateGroupDataAndStorage: setNewActiveGroup,
        removeActiveSocialProfileFromGroup: removeNewActiveSocialProfileFromGroup,
        removeActiveGroupIfNoProfile: resetActiveGroup,
        forcedActiveGroup: setNewActiveGroup
    };
};

export default useActiveGroup;
